// @flow
import { LOCATION_CHANGE } from "connected-react-router";
import {
  AuthenticateSuccess,
  AuthenticateError,
  ToggleTermsOfUseModal,
  SetPassword,
  SetPasswordSuccess,
  SetPasswordError,
  SetAccessTokenExpiresAt,
  Impersonate,
  ImpersonateSuccess,
  ImpersonateError,
  ToggleContactModal,
  GetAccountClientServiceSettingsSuccess,
  GetAccountAbsenceManagementSettingsSuccess,
} from "../constants/actions";
import type { Profile } from "../types";
import { cloneObject } from "@utils/object";
import type { ServiceSettings } from "@modules/service/reducer/service-settings/types";
import type { ClientSettingsSettingsType } from "@modules/settings/types";

export type LoginState = $Exact<{
  authChecked: boolean,
  loggedIn: boolean,
  isLoading: boolean,
  profile: Profile | null,
  error: string | null,
  showMailSent: boolean,
  impersonateLogin: boolean,
  modals: $Exact<{
    termsOfUse: $Exact<{
      visible: boolean,
    }>,
    contact: $Exact<{
      visible: boolean,
    }>,
  }>,
  clientServiceSettings: ServiceSettings | null,
  clientAbsenceManagementSettings: ClientSettingsSettingsType | null,
}>;

export const initialState: LoginState = {
  authChecked: false,
  loggedIn: false,
  isLoading: false,
  profile: null,
  error: null,
  showMailSent: false,
  impersonateLogin: false,
  modals: {
    termsOfUse: {
      visible: false,
    },
    contact: {
      visible: false,
    },
  },
  clientServiceSettings: null,
  clientAbsenceManagementSettings: null,
};

const onLocationChange = (state: LoginState) => () => {
  return { ...state, isLoading: false, error: null };
};

const onForgotPassword = (state: LoginState): (() => LoginState) => () => {
  return { ...state, isLoading: true };
};

const onAuthenticateSuccess = (state: LoginState) => ({ payload }: Object) => {
  return {
    ...state,
    authChecked: true,
    loggedIn: true,
    error: null,
    isLoading: false,
    profile: payload,
  };
};

const onAuthenticateError = (state: LoginState) => ({ payload }: Object) => {
  return {
    ...state,
    authChecked: true,
    profile: null,
    error: null,
    loggedIn: false,
    isLoading: false,
  };
};

export const onToggleTermsOfUseModal = (state: LoginState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      termsOfUse: {
        ...state.modals.termsOfUse,
        visible: payload,
      },
    },
  };
};

export const onSetPassword = (state: LoginState): (() => LoginState) => () => {
  return { ...state, isLoading: true };
};

export const onSetPasswordSuccess = (state: LoginState) => () => {
  return { ...state, isLoading: false, error: null };
};

export const onSetPasswordError = (state: LoginState) => ({ payload }: Object) => {
  return { ...state, isLoading: false, error: payload };
};

export const onImpersonate = (state: LoginState): (() => LoginState) => () => {
  return { ...state, isLoading: true };
};

export const onImpersonateSuccess = (state: LoginState) => () => {
  return {
    ...state,
    isLoading: false,
    error: null,
    impersonateLogin: true,
  };
};

export const onImpersonateError = (state: LoginState) => ({ payload }: Object) => {
  return { ...state, isLoading: false, error: payload };
};

export const onSetAccessTokenExpiresAt = (state: LoginState) => ({ payload }: Object) => {
  return {
    ...state,
    profile: {
      ...state.profile,
      accessTokenExpiresAt: payload,
    },
  };
};

export const onToggleContactModal = (state: LoginState) => () => ({
  ...state,
  modals: {
    ...state.modals,
    contact: {
      visible: !state.modals.contact.visible,
    },
  },
});

export const onGetAccountClientServiceSettingsSuccess = (state: LoginState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    clientServiceSettings: payload,
  };
};

export const onGetAccountAbsenceManagementSettingsSuccess = (state: LoginState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    clientAbsenceManagementSettings: payload,
  };
};

export default (state: LoginState = initialState, action: Object) => {
  const act = {
    [AuthenticateSuccess]: onAuthenticateSuccess,
    [AuthenticateError]: onAuthenticateError,
    [ToggleTermsOfUseModal]: onToggleTermsOfUseModal,
    [SetPassword]: onSetPassword,
    [SetPasswordSuccess]: onSetPasswordSuccess,
    [SetPasswordError]: onSetPasswordError,
    [SetAccessTokenExpiresAt]: onSetAccessTokenExpiresAt,
    [Impersonate]: onImpersonate,
    [ImpersonateSuccess]: onImpersonateSuccess,
    [ImpersonateError]: onImpersonateError,
    [ToggleContactModal]: onToggleContactModal,
    [GetAccountClientServiceSettingsSuccess]: onGetAccountClientServiceSettingsSuccess,
    [GetAccountAbsenceManagementSettingsSuccess]: onGetAccountAbsenceManagementSettingsSuccess,
    [LOCATION_CHANGE]: onLocationChange,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
